import { logger } from "@origin-digital/reporting-client";
import {
  LevelType,
  PostalDeliveryType,
  StreetSuffix,
  StreetType,
  UnitType,
} from "../../crm/lpg/graphql-types/globalTypes";

interface PafValueMap {
  name: string;
  pafValue: string;
  krakenValue: string;
  lpgValue: string;
}

export const getPafMapping = (
  value: string | null | undefined,
  mapping: PafValueMap[]
): PafValueMap | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  const foundMapping = mapping.find((i) => i.pafValue === value);
  if (foundMapping === undefined) {
    logger.error(`Cannot map address type from PAF value: "${value}"`);
    return undefined;
  }
  return foundMapping;
};

export const mapPafTypeToKrakenType = (
  value: string | null | undefined,
  mapping: PafValueMap[]
): string | undefined => {
  return getPafMapping(value, mapping)?.krakenValue;
};

export const mapPafTypeToLPGType = (
  value: string | null | undefined,
  mapping: PafValueMap[]
): string | undefined => {
  return getPafMapping(value, mapping)?.lpgValue;
};

export const unitTypeMapping: PafValueMap[] = [
  {
    name: "Apartment",
    pafValue: "APT",
    krakenValue: "APT",
    lpgValue: UnitType.APARTMENT,
  },
  {
    name: "Cottage",
    pafValue: "CTGE",
    krakenValue: "CTGE",
    lpgValue: UnitType.COTTAGE,
  },
  {
    name: "Duplex",
    pafValue: "DUP",
    krakenValue: "DUP",
    lpgValue: UnitType.DUPLEX,
  },
  {
    name: "Factory",
    pafValue: "FY",
    krakenValue: "FY",
    lpgValue: UnitType.FACTORY,
  },
  {
    name: "Flat",
    pafValue: "F",
    krakenValue: "F",
    lpgValue: UnitType.FLAT,
  },
  {
    name: "House",
    pafValue: "HSE",
    krakenValue: "HSE",
    lpgValue: UnitType.HOUSE,
  },
  {
    name: "Kiosk",
    pafValue: "KSK",
    krakenValue: "KSK",
    lpgValue: UnitType.KIOSK,
  },
  {
    name: "Maisonette",
    pafValue: "MSNT",
    krakenValue: "MSNT",
    lpgValue: UnitType.MAISONETTE,
  },
  {
    name: "Marine Berth",
    pafValue: "MB",
    krakenValue: "MB",
    lpgValue: UnitType.MARINE_BERTH,
  },
  {
    name: "Office",
    pafValue: "OFF",
    krakenValue: "OFF",
    lpgValue: UnitType.OFFICE,
  },
  {
    name: "Penthouse",
    pafValue: "PTHS",
    krakenValue: "PTHS",
    lpgValue: UnitType.PENTHOUSE,
  },
  {
    name: "Rear",
    pafValue: "R",
    krakenValue: "REAR",
    lpgValue: UnitType.REAR,
  },
  {
    name: "Room",
    pafValue: "RM",
    krakenValue: "RM",
    lpgValue: UnitType.ROOM,
  },
  {
    name: "Shed",
    pafValue: "SHED",
    krakenValue: "SHED",
    lpgValue: UnitType.SHED,
  },
  {
    name: "Shop",
    pafValue: "SHOP",
    krakenValue: "SHOP",
    lpgValue: UnitType.SHOP,
  },
  {
    name: "Site",
    pafValue: "SITE",
    krakenValue: "SITE",
    lpgValue: UnitType.SITE,
  },
  {
    name: "Stall",
    pafValue: "SL",
    krakenValue: "SL",
    lpgValue: UnitType.STALL,
  },
  {
    name: "Studio",
    pafValue: "STU",
    krakenValue: "STU",
    lpgValue: UnitType.STUDIO,
  },
  {
    name: "Suite",
    pafValue: "SE",
    krakenValue: "SE",
    lpgValue: UnitType.SUITE,
  },
  {
    name: "Townhouse",
    pafValue: "TNHS",
    krakenValue: "TNHS",
    lpgValue: UnitType.TOWNHOUSE,
  },
  {
    name: "Unit",
    pafValue: "U",
    krakenValue: "U",
    lpgValue: UnitType.UNIT,
  },
  {
    name: "Villa",
    pafValue: "VLLA",
    krakenValue: "VLLA",
    lpgValue: UnitType.VILLA,
  },
  {
    name: "Ward",
    pafValue: "WARD",
    krakenValue: "WARD",
    lpgValue: UnitType.WARD,
  },
  {
    name: "Warehouse",
    pafValue: "WE",
    krakenValue: "WE",
    lpgValue: UnitType.WAREHOUSE,
  },
];

export const floorTypeMapping: PafValueMap[] = [
  {
    name: "Basement",
    pafValue: "B",
    krakenValue: "B",
    lpgValue: LevelType.BASEMENT,
  },
  {
    name: "Floor",
    pafValue: "FL",
    krakenValue: "FL",
    lpgValue: LevelType.FLOOR,
  },
  {
    name: "Ground Floor",
    pafValue: "G",
    krakenValue: "G",
    lpgValue: LevelType.GROUND_FLOOR,
  },
  {
    name: "Level",
    pafValue: "L",
    krakenValue: "L",
    lpgValue: LevelType.LEVEL,
  },
  {
    name: "Lower Ground Floor",
    pafValue: "LG",
    krakenValue: "LG",
    lpgValue: LevelType.LOWER_GROUND,
  },
  {
    name: "Mezzanine",
    pafValue: "M",
    krakenValue: "M",
    lpgValue: LevelType.MEZZANINE,
  },
  {
    name: "Upper Ground Floor",
    pafValue: "UG",
    krakenValue: "UG",
    lpgValue: LevelType.UPPER_GROUND,
  },
];

export const streetSuffixTypeMapping: PafValueMap[] = [
  {
    name: "Central",
    pafValue: "CN",
    krakenValue: "CN",
    lpgValue: StreetSuffix.CENTRAL,
  },
  {
    name: "East",
    pafValue: "E",
    krakenValue: "E",
    lpgValue: StreetSuffix.EAST,
  },
  {
    name: "Extension",
    pafValue: "EX",
    krakenValue: "EX",
    lpgValue: StreetSuffix.EXTENSION,
  },
  {
    name: "Lower",
    pafValue: "LR",
    krakenValue: "LR",
    lpgValue: StreetSuffix.LOWER,
  },
  {
    name: "North East",
    pafValue: "NE",
    krakenValue: "NE",
    lpgValue: StreetSuffix.NORTH_EAST,
  },
  {
    name: "North West",
    pafValue: "NW",
    krakenValue: "NW",
    lpgValue: StreetSuffix.NORTH_WEST,
  },
  {
    name: "North",
    pafValue: "N",
    krakenValue: "N",
    lpgValue: StreetSuffix.NORTH,
  },
  {
    name: "South East",
    pafValue: "SE",
    krakenValue: "SE",
    lpgValue: StreetSuffix.SOUTH_EAST,
  },
  {
    name: "South West",
    pafValue: "SW",
    krakenValue: "SW",
    lpgValue: StreetSuffix.SOUTH_WEST,
  },
  {
    name: "South",
    pafValue: "S",
    krakenValue: "S",
    lpgValue: StreetSuffix.SOUTH,
  },
  {
    name: "Upper",
    pafValue: "UP",
    krakenValue: "UP",
    lpgValue: StreetSuffix.UPPER,
  },
  {
    name: "West",
    pafValue: "W",
    krakenValue: "W",
    lpgValue: StreetSuffix.WEST,
  },
];

export const postalDeliveryTypeMapping: PafValueMap[] = [
  {
    name: "Care Of Post Office / Poste Restante",
    pafValue: "CARE PO",
    krakenValue: "CARE PO",
    lpgValue: PostalDeliveryType.CARE_OF_POST_OFFICE,
  },
  {
    name: "Community Mail Agent",
    pafValue: "CMA",
    krakenValue: "CMA",
    lpgValue: PostalDeliveryType.COMMUNITY_MAIL_AGENT,
  },
  {
    name: "Community Mail Bag",
    pafValue: "CMB",
    krakenValue: "CMB",
    lpgValue: PostalDeliveryType.COMMUNITY_MAIL_BAG,
  },
  {
    name: "Community Postal Agent",
    pafValue: "CPA",
    krakenValue: "CPA",
    lpgValue: PostalDeliveryType.COMMUNITY_POSTAL_AGENT,
  },
  {
    name: "General Post Office Box",
    pafValue: "GPO BOX",
    krakenValue: "GPO BOX",
    lpgValue: PostalDeliveryType.GPO_BOX,
  },
  {
    name: "Locked Mail Bag Service",
    pafValue: "LOCKED BAG",
    krakenValue: "LOCKED BAG",
    lpgValue: PostalDeliveryType.LOCKED_BAG,
  },
  {
    name: "Mail Service",
    pafValue: "MS",
    krakenValue: "MS",
    lpgValue: PostalDeliveryType.MAIL_SERVICE,
  },
  {
    name: "Post Office Box",
    pafValue: "PO BOX",
    krakenValue: "PO BOX",
    lpgValue: PostalDeliveryType.PO_BOX,
  },
  {
    name: "Private Mail Bag Service",
    pafValue: "PRIVATE BAG",
    krakenValue: "PRIVATE BAG",
    lpgValue: PostalDeliveryType.PRIVATE_BAG,
  },
  {
    name: "Roadside Delivery",
    pafValue: "RSD",
    krakenValue: "RSD",
    lpgValue: PostalDeliveryType.ROADSIDE_DELIVERY,
  },
  {
    name: "Roadside Mail Bag / Box",
    pafValue: "RMB",
    krakenValue: "RMB",
    lpgValue: PostalDeliveryType.ROADSIDE_MAIL_BAG,
  },
  {
    name: "Roadside Mail Service",
    pafValue: "RMS",
    krakenValue: "RMS",
    lpgValue: PostalDeliveryType.ROADSIDE_MAIL_SERVICE,
  },
];

export const streetTypeMapping: PafValueMap[] = [
  {
    name: "Access",
    pafValue: "ACCS",
    krakenValue: "ACCS",
    lpgValue: StreetType.ACCESS,
  },
  {
    name: "Alley",
    pafValue: "ALLY",
    krakenValue: "ALLY",
    lpgValue: StreetType.ALLEY,
  },
  {
    name: "Alleyway",
    pafValue: "ALWY",
    krakenValue: "ALWY",
    lpgValue: StreetType.ALLEYWAY,
  },
  {
    name: "Amble",
    pafValue: "AMBL",
    krakenValue: "AMBL",
    lpgValue: StreetType.AMBLE,
  },
  {
    name: "Anchorage",
    pafValue: "ANCG",
    krakenValue: "ANCG",
    lpgValue: StreetType.ANCHORAGE,
  },
  {
    name: "Approach",
    pafValue: "APP",
    krakenValue: "APP",
    lpgValue: StreetType.APPROACH,
  },
  {
    name: "Arcade",
    pafValue: "ARC",
    krakenValue: "ARC",
    lpgValue: StreetType.ARCADE,
  },
  {
    name: "Artery",
    pafValue: "ART",
    krakenValue: "ART",
    lpgValue: StreetType.ARTERY,
  },
  {
    name: "Avenue",
    pafValue: "AVE",
    krakenValue: "AVE",
    lpgValue: StreetType.AVENUE,
  },
  {
    name: "Basin",
    pafValue: "BASN",
    krakenValue: "BASN",
    lpgValue: StreetType.BASIN,
  },
  {
    name: "Beach",
    pafValue: "BCH",
    krakenValue: "BCH",
    lpgValue: StreetType.BEACH,
  },
  {
    name: "Bend",
    pafValue: "BEND",
    krakenValue: "BEND",
    lpgValue: StreetType.BEND,
  },
  {
    name: "Block",
    pafValue: "BLK",
    krakenValue: "BLK",
    lpgValue: StreetType.BLOCK,
  },
  {
    name: "Boulevard",
    pafValue: "BVD",
    krakenValue: "BVD",
    lpgValue: StreetType.BOULEVARD,
  },
  {
    name: "Brace",
    pafValue: "BRCE",
    krakenValue: "BRCE",
    lpgValue: StreetType.BRACE,
  },
  {
    name: "Brae",
    pafValue: "BRAE",
    krakenValue: "BRAE",
    lpgValue: StreetType.BRAE,
  },
  {
    name: "Break",
    pafValue: "BRK",
    krakenValue: "BRK",
    lpgValue: StreetType.BREAK,
  },
  {
    name: "Bridge",
    pafValue: "BDGE",
    krakenValue: "BDGE",
    lpgValue: StreetType.BRIDGE,
  },
  {
    name: "Broadway",
    pafValue: "BDWY",
    krakenValue: "BDWY",
    lpgValue: StreetType.BROADWAY,
  },
  {
    name: "Brow",
    pafValue: "BROW",
    krakenValue: "BROW",
    lpgValue: StreetType.BROW,
  },
  {
    name: "Bypass",
    pafValue: "BYPA",
    krakenValue: "BYPA",
    lpgValue: StreetType.BYPASS,
  },
  {
    name: "Byway",
    pafValue: "BYWY",
    krakenValue: "BYWY",
    lpgValue: StreetType.BYWAY,
  },
  {
    name: "Causeway",
    pafValue: "CAUS",
    krakenValue: "CAUS",
    lpgValue: StreetType.CAUSEWAY,
  },
  {
    name: "Centre",
    pafValue: "CTR",
    krakenValue: "CTR",
    lpgValue: StreetType.CENTRE,
  },
  {
    name: "Centreway",
    pafValue: "CNWY",
    krakenValue: "CNWY",
    lpgValue: StreetType.CENTREWAY,
  },
  {
    name: "Chase",
    pafValue: "CH",
    krakenValue: "CH",
    lpgValue: StreetType.CHASE,
  },
  {
    name: "Circle",
    pafValue: "CIR",
    krakenValue: "CIR",
    lpgValue: StreetType.CIRCLE,
  },
  {
    name: "Circlet",
    pafValue: "CLT",
    krakenValue: "CLT",
    lpgValue: StreetType.CIRCLET,
  },
  {
    name: "Circuit",
    pafValue: "CCT",
    krakenValue: "CCT",
    lpgValue: StreetType.CIRCUIT,
  },
  {
    name: "Circus",
    pafValue: "CRCS",
    krakenValue: "CRCS",
    lpgValue: StreetType.CIRCUS,
  },
  {
    name: "Close",
    pafValue: "CL",
    krakenValue: "CL",
    lpgValue: StreetType.CLOSE,
  },
  {
    name: "Colonnade",
    pafValue: "CLDE",
    krakenValue: "CLDE",
    lpgValue: StreetType.COLONNADE,
  },
  {
    name: "Common",
    pafValue: "CMMN",
    krakenValue: "CMMN",
    lpgValue: StreetType.COMMON,
  },
  {
    name: "Concourse",
    pafValue: "CON",
    krakenValue: "CON",
    lpgValue: StreetType.CONCOURSE,
  },
  {
    name: "Copse",
    pafValue: "CPS",
    krakenValue: "CPS",
    lpgValue: StreetType.COPSE,
  },
  {
    name: "Corner",
    pafValue: "CNR",
    krakenValue: "CNR",
    lpgValue: StreetType.CORNER,
  },
  {
    name: "Corso",
    pafValue: "CSO",
    krakenValue: "CSO",
    lpgValue: StreetType.CORSO,
  },
  {
    name: "Court",
    pafValue: "CT",
    krakenValue: "CT",
    lpgValue: StreetType.COURT,
  },
  {
    name: "Courtyard",
    pafValue: "CTYD",
    krakenValue: "CTYD",
    lpgValue: StreetType.COURTYARD,
  },
  {
    name: "Cove",
    pafValue: "COVE",
    krakenValue: "COVE",
    lpgValue: StreetType.COVE,
  },
  {
    name: "Crescent",
    pafValue: "CRES",
    krakenValue: "CRES",
    lpgValue: StreetType.CRESCENT,
  },
  {
    name: "Crest",
    pafValue: "CRST",
    krakenValue: "CRST",
    lpgValue: StreetType.CREST,
  },
  {
    name: "Cross",
    pafValue: "CRSS",
    krakenValue: "CRSS",
    lpgValue: StreetType.CROSS,
  },
  {
    name: "Crossing",
    pafValue: "CRSG",
    krakenValue: "CRSG",
    lpgValue: StreetType.CROSSING,
  },
  {
    name: "Crossroad",
    pafValue: "CRD",
    krakenValue: "CRD",
    lpgValue: StreetType.CROSSROAD,
  },
  {
    name: "Crossway",
    pafValue: "COWY",
    krakenValue: "COWY",
    lpgValue: StreetType.CROSSWAY,
  },
  {
    name: "Cruiseway",
    pafValue: "CUWY",
    krakenValue: "CUWY",
    lpgValue: StreetType.CRUISEWAY,
  },
  {
    name: "Cul-De-Sac",
    pafValue: "CDS",
    krakenValue: "CDS",
    lpgValue: StreetType.CUL_DE_SAC,
  },
  {
    name: "Cutting",
    pafValue: "CTTG",
    krakenValue: "CTTG",
    lpgValue: StreetType.CUTTING,
  },
  {
    name: "Dale",
    pafValue: "DALE",
    krakenValue: "DALE",
    lpgValue: StreetType.DALE,
  },
  {
    name: "Dell",
    pafValue: "DELL",
    krakenValue: "DELL",
    lpgValue: StreetType.DELL,
  },
  {
    name: "Deviation",
    pafValue: "DEVN",
    krakenValue: "DEVN",
    lpgValue: StreetType.DEVIATION,
  },
  {
    name: "Dip",
    pafValue: "DIP",
    krakenValue: "DIP",
    lpgValue: StreetType.DIP,
  },
  {
    name: "Distributor",
    pafValue: "DSTR",
    krakenValue: "DSTR",
    lpgValue: StreetType.DISTRIBUTOR,
  },
  {
    name: "Drive",
    pafValue: "DR",
    krakenValue: "DR",
    lpgValue: StreetType.DRIVE,
  },
  {
    name: "Driveway",
    pafValue: "DRWY",
    krakenValue: "DRWY",
    lpgValue: StreetType.DRIVEWAY,
  },
  {
    name: "Edge",
    pafValue: "EDGE",
    krakenValue: "EDGE",
    lpgValue: StreetType.EDGE,
  },
  {
    name: "Elbow",
    pafValue: "ELB",
    krakenValue: "ELB",
    lpgValue: StreetType.ELBOW,
  },
  {
    name: "End",
    pafValue: "END",
    krakenValue: "END",
    lpgValue: StreetType.END,
  },
  {
    name: "Entrance",
    pafValue: "ENT",
    krakenValue: "ENT",
    lpgValue: StreetType.ENTRANCE,
  },
  {
    name: "Esplanade",
    pafValue: "ESP",
    krakenValue: "ESP",
    lpgValue: StreetType.ESPLANADE,
  },
  {
    name: "Estate",
    pafValue: "EST",
    krakenValue: "EST",
    lpgValue: StreetType.ESTATE,
  },
  {
    name: "Expressway",
    pafValue: "EXP",
    krakenValue: "EXP",
    lpgValue: StreetType.EXPRESSWAY,
  },
  {
    name: "Extension",
    pafValue: "EXTN",
    krakenValue: "EXTN",
    lpgValue: StreetType.EXTENSION,
  },
  {
    name: "Fairway",
    pafValue: "FAWY",
    krakenValue: "FAWY",
    lpgValue: StreetType.FAIRWAY,
  },
  {
    name: "Fire Track",
    pafValue: "FTRK",
    krakenValue: "FTRK",
    lpgValue: StreetType.FIRE_TRACK,
  },
  {
    name: "Firetrail",
    pafValue: "FITR",
    krakenValue: "FITR",
    lpgValue: StreetType.FIRETRAIL,
  },
  {
    name: "Flat",
    pafValue: "FLAT",
    krakenValue: "FLAT",
    lpgValue: StreetType.FLAT,
  },
  {
    name: "Follow",
    pafValue: "FOLW",
    krakenValue: "FOLW",
    lpgValue: StreetType.FOLLOW,
  },
  {
    name: "Footway",
    pafValue: "FTWY",
    krakenValue: "FTWY",
    lpgValue: StreetType.FOOTWAY,
  },
  {
    name: "Foreshore",
    pafValue: "FSHR",
    krakenValue: "FSHR",
    lpgValue: StreetType.FORESHORE,
  },
  {
    name: "Formation",
    pafValue: "FORM",
    krakenValue: "FORM",
    lpgValue: StreetType.FORMATION,
  },
  {
    name: "Freeway",
    pafValue: "FWY",
    krakenValue: "FWY",
    lpgValue: StreetType.FREEWAY,
  },
  {
    name: "Front",
    pafValue: "FRNT",
    krakenValue: "FRNT",
    lpgValue: StreetType.FRONT,
  },
  {
    name: "Frontage",
    pafValue: "FRTG",
    krakenValue: "FRTG",
    lpgValue: StreetType.FRONTAGE,
  },
  {
    name: "Gap",
    pafValue: "GAP",
    krakenValue: "GAP",
    lpgValue: StreetType.GAP,
  },
  {
    name: "Garden",
    pafValue: "GDN",
    krakenValue: "GDN",
    lpgValue: StreetType.GARDEN,
  },
  {
    name: "Gardens",
    pafValue: "GDNS",
    krakenValue: "GDNS",
    lpgValue: StreetType.GARDENS,
  },
  {
    name: "Gate",
    pafValue: "GTE",
    krakenValue: "GTE",
    lpgValue: StreetType.GATE,
  },
  {
    name: "Gates",
    pafValue: "GTES",
    krakenValue: "GTES",
    lpgValue: StreetType.GATES,
  },
  {
    name: "Glade",
    pafValue: "GLD",
    krakenValue: "GLD",
    lpgValue: StreetType.GLADE,
  },
  {
    name: "Glen",
    pafValue: "GLEN",
    krakenValue: "GLEN",
    lpgValue: StreetType.GLEN,
  },
  {
    name: "Grange",
    pafValue: "GRA",
    krakenValue: "GRA",
    lpgValue: StreetType.GRANGE,
  },
  {
    name: "Green",
    pafValue: "GRN",
    krakenValue: "GRN",
    lpgValue: StreetType.GREEN,
  },
  {
    name: "Ground",
    pafValue: "GRND",
    krakenValue: "GRND",
    lpgValue: StreetType.GROUND,
  },
  {
    name: "Grove",
    pafValue: "GR",
    krakenValue: "GR",
    lpgValue: StreetType.GROVE,
  },
  {
    name: "Gully",
    pafValue: "GLY",
    krakenValue: "GLY",
    lpgValue: StreetType.GULLY,
  },
  {
    name: "Heights",
    pafValue: "HTS",
    krakenValue: "HTS",
    lpgValue: StreetType.HEIGHTS,
  },
  {
    name: "Highroad",
    pafValue: "HRD",
    krakenValue: "HRD",
    lpgValue: StreetType.HIGHROAD,
  },
  {
    name: "Highway",
    pafValue: "HWY",
    krakenValue: "HWY",
    lpgValue: StreetType.HIGHWAY,
  },
  {
    name: "Hill",
    pafValue: "HILL",
    krakenValue: "HILL",
    lpgValue: StreetType.HILL,
  },
  {
    name: "Interchange",
    pafValue: "INTG",
    krakenValue: "INTG",
    lpgValue: StreetType.INTERCHANGE,
  },
  {
    name: "Intersection",
    pafValue: "INTN",
    krakenValue: "INTN",
    lpgValue: StreetType.INTERSECTION,
  },
  {
    name: "Junction",
    pafValue: "JNC",
    krakenValue: "JNC",
    lpgValue: StreetType.JUNCTION,
  },
  {
    name: "Key",
    pafValue: "KEY",
    krakenValue: "KEY",
    lpgValue: StreetType.KEY,
  },
  {
    name: "Landing",
    pafValue: "LDG",
    krakenValue: "LDG",
    lpgValue: StreetType.LANDING,
  },
  {
    name: "Lane",
    pafValue: "LANE",
    krakenValue: "LANE",
    lpgValue: StreetType.LANE,
  },
  {
    name: "Laneway",
    pafValue: "LNWY",
    krakenValue: "LNWY",
    lpgValue: StreetType.LANEWAY,
  },
  {
    name: "Lees",
    pafValue: "LEES",
    krakenValue: "LEES",
    lpgValue: StreetType.LEES,
  },
  {
    name: "Line",
    pafValue: "LINE",
    krakenValue: "LINE",
    lpgValue: StreetType.LINE,
  },
  {
    name: "Link",
    pafValue: "LINK",
    krakenValue: "LINK",
    lpgValue: StreetType.LINK,
  },
  {
    name: "Little",
    pafValue: "LT",
    krakenValue: "LT",
    lpgValue: StreetType.LITTLE,
  },
  {
    name: "Lookout",
    pafValue: "LKT",
    krakenValue: "LKT",
    lpgValue: StreetType.LOOKOUT,
  },
  {
    name: "Loop",
    pafValue: "LOOP",
    krakenValue: "LOOP",
    lpgValue: StreetType.LOOP,
  },
  {
    name: "Lower",
    pafValue: "LWR",
    krakenValue: "LWR",
    lpgValue: StreetType.LOWER,
  },
  {
    name: "Mall",
    pafValue: "MALL",
    krakenValue: "MALL",
    lpgValue: StreetType.MALL,
  },
  {
    name: "Meander",
    pafValue: "MNDR",
    krakenValue: "MNDR",
    lpgValue: StreetType.MEANDER,
  },
  {
    name: "Mew",
    pafValue: "MEW",
    krakenValue: "MEW",
    lpgValue: StreetType.MEW,
  },
  {
    name: "Mews",
    pafValue: "MEWS",
    krakenValue: "MEWS",
    lpgValue: StreetType.MEWS,
  },
  {
    name: "Motorway",
    pafValue: "MWY",
    krakenValue: "MWY",
    lpgValue: StreetType.MOTORWAY,
  },
  {
    name: "Mount",
    pafValue: "MT",
    krakenValue: "MT",
    lpgValue: StreetType.MOUNT,
  },
  {
    name: "Nook",
    pafValue: "NOOK",
    krakenValue: "NOOK",
    lpgValue: StreetType.NOOK,
  },
  {
    name: "Outlook",
    pafValue: "OTLK",
    krakenValue: "OTLK",
    lpgValue: StreetType.OUTLOOK,
  },
  {
    name: "Parade",
    pafValue: "PDE",
    krakenValue: "PDE",
    lpgValue: StreetType.PARADE,
  },
  {
    name: "Park",
    pafValue: "PARK",
    krakenValue: "PARK",
    lpgValue: StreetType.PARK,
  },
  {
    name: "Parklands",
    pafValue: "PKLD",
    krakenValue: "PKLD",
    lpgValue: StreetType.PARKLANDS,
  },
  {
    name: "Parkway",
    pafValue: "PKWY",
    krakenValue: "PKWY",
    lpgValue: StreetType.PARKWAY,
  },
  {
    name: "Part",
    pafValue: "PART",
    krakenValue: "PART",
    lpgValue: StreetType.PART,
  },
  {
    name: "Pass",
    pafValue: "PASS",
    krakenValue: "PASS",
    lpgValue: StreetType.PASS,
  },
  {
    name: "Passage",
    pafValue: "PSGE",
    krakenValue: "PSGE",
    lpgValue: StreetType.PASSAGE,
  },
  {
    name: "Path",
    pafValue: "PATH",
    krakenValue: "PATH",
    lpgValue: StreetType.PATH,
  },
  {
    name: "Pathway",
    pafValue: "PHWY",
    krakenValue: "PHWY",
    lpgValue: StreetType.PATHWAY,
  },
  {
    name: "Piazza",
    pafValue: "PIAZ",
    krakenValue: "PIAZ",
    lpgValue: StreetType.PIAZZA,
  },
  {
    name: "Place",
    pafValue: "PL",
    krakenValue: "PL",
    lpgValue: StreetType.PLACE,
  },
  {
    name: "Plateau",
    pafValue: "PLAT",
    krakenValue: "PLAT",
    lpgValue: StreetType.PLATEAU,
  },
  {
    name: "Plaza",
    pafValue: "PLZA",
    krakenValue: "PLZA",
    lpgValue: StreetType.PLAZA,
  },
  {
    name: "Pocket",
    pafValue: "PKT",
    krakenValue: "PKT",
    lpgValue: StreetType.POCKET,
  },
  {
    name: "Point",
    pafValue: "PNT",
    krakenValue: "PNT",
    lpgValue: StreetType.POINT,
  },
  {
    name: "Port",
    pafValue: "PORT",
    krakenValue: "PORT",
    lpgValue: StreetType.PORT,
  },
  {
    name: "Promenade",
    pafValue: "PROM",
    krakenValue: "PROM",
    lpgValue: StreetType.PROMENADE,
  },
  {
    name: "Quad",
    pafValue: "QUAD",
    krakenValue: "QUAD",
    lpgValue: StreetType.QUAD,
  },
  {
    name: "Quadrangle",
    pafValue: "QDGL",
    krakenValue: "QDGL",
    lpgValue: StreetType.QUADRANGLE,
  },
  {
    name: "Quadrant",
    pafValue: "QDRT",
    krakenValue: "QDRT",
    lpgValue: StreetType.QUADRANT,
  },
  {
    name: "Quay",
    pafValue: "QY",
    krakenValue: "QY",
    lpgValue: StreetType.QUAY,
  },
  {
    name: "Quays",
    pafValue: "QYS",
    krakenValue: "QYS",
    lpgValue: StreetType.QUAYS,
  },
  {
    name: "Ramble",
    pafValue: "RMBL",
    krakenValue: "RMBL",
    lpgValue: StreetType.RAMBLE,
  },
  {
    name: "Ramp",
    pafValue: "RAMP",
    krakenValue: "RAMP",
    lpgValue: StreetType.RAMP,
  },
  {
    name: "Range",
    pafValue: "RNGE",
    krakenValue: "RNGE",
    lpgValue: StreetType.RANGE,
  },
  {
    name: "Reach",
    pafValue: "RCH",
    krakenValue: "RCH",
    lpgValue: StreetType.REACH,
  },
  {
    name: "Reserve",
    pafValue: "RES",
    krakenValue: "RES",
    lpgValue: StreetType.RESERVE,
  },
  {
    name: "Rest",
    pafValue: "REST",
    krakenValue: "REST",
    lpgValue: StreetType.REST,
  },
  {
    name: "Retreat",
    pafValue: "RTT",
    krakenValue: "RTT",
    lpgValue: StreetType.RETREAT,
  },
  {
    name: "Ride",
    pafValue: "RIDE",
    krakenValue: "RIDE",
    lpgValue: StreetType.RIDE,
  },
  {
    name: "Ridge",
    pafValue: "RDGE",
    krakenValue: "RDGE",
    lpgValue: StreetType.RIDGE,
  },
  {
    name: "Ridgeway",
    pafValue: "RGWY",
    krakenValue: "RGWY",
    lpgValue: StreetType.RIDGEWAY,
  },
  {
    name: "Right of way",
    pafValue: "ROWY",
    krakenValue: "ROWY",
    lpgValue: StreetType.RIGHT_OF_WAY,
  },
  {
    name: "Ring",
    pafValue: "RING",
    krakenValue: "RING",
    lpgValue: StreetType.RING,
  },
  {
    name: "Rise",
    pafValue: "RISE",
    krakenValue: "RISE",
    lpgValue: StreetType.RISE,
  },
  {
    name: "River",
    pafValue: "RVR",
    krakenValue: "RVR",
    lpgValue: StreetType.RIVER,
  },
  {
    name: "Riverway",
    pafValue: "RVWY",
    krakenValue: "RVWY",
    lpgValue: StreetType.RIVERWAY,
  },
  {
    name: "Riviera",
    pafValue: "RVRA",
    krakenValue: "RVRA",
    lpgValue: StreetType.RIVIERA,
  },
  {
    name: "Road",
    pafValue: "RD",
    krakenValue: "RD",
    lpgValue: StreetType.ROAD,
  },
  {
    name: "Roads",
    pafValue: "RDS",
    krakenValue: "RDS",
    lpgValue: StreetType.ROADS,
  },
  {
    name: "Roadside",
    pafValue: "RDSD",
    krakenValue: "RDSD",
    lpgValue: StreetType.ROADSIDE,
  },
  {
    name: "Roadway",
    pafValue: "RDWY",
    krakenValue: "RDWY",
    lpgValue: StreetType.ROADWAY,
  },
  {
    name: "Ronde",
    pafValue: "RNDE",
    krakenValue: "RNDE",
    lpgValue: StreetType.RONDE,
  },
  {
    name: "Rosebowl",
    pafValue: "RSBL",
    krakenValue: "RSBL",
    lpgValue: StreetType.ROSEBOWL,
  },
  {
    name: "Rotary",
    pafValue: "RTY",
    krakenValue: "RTY",
    lpgValue: StreetType.ROTARY,
  },
  {
    name: "Round",
    pafValue: "RND",
    krakenValue: "RND",
    lpgValue: StreetType.ROUND,
  },
  {
    name: "Route",
    pafValue: "RTE",
    krakenValue: "RTE",
    lpgValue: StreetType.ROUTE,
  },
  {
    name: "Row",
    pafValue: "ROW",
    krakenValue: "ROW",
    lpgValue: StreetType.ROW,
  },
  {
    name: "Rue",
    pafValue: "RUE",
    krakenValue: "RUE",
    lpgValue: StreetType.RUE,
  },
  {
    name: "Run",
    pafValue: "RUN",
    krakenValue: "RUN",
    lpgValue: StreetType.RUN,
  },
  {
    name: "Service way",
    pafValue: "SWY",
    krakenValue: "SWY",
    lpgValue: StreetType.SERVICE_WAY,
  },
  {
    name: "Siding",
    pafValue: "SDNG",
    krakenValue: "SDNG",
    lpgValue: StreetType.SIDING,
  },
  {
    name: "Slope",
    pafValue: "SLPE",
    krakenValue: "SLPE",
    lpgValue: StreetType.SLOPE,
  },
  {
    name: "Sound",
    pafValue: "SND",
    krakenValue: "SND",
    lpgValue: StreetType.SOUND,
  },
  {
    name: "Spur",
    pafValue: "SPUR",
    krakenValue: "SPUR",
    lpgValue: StreetType.SPUR,
  },
  {
    name: "Square",
    pafValue: "SQ",
    krakenValue: "SQ",
    lpgValue: StreetType.SQUARE,
  },
  {
    name: "Stairs",
    pafValue: "STRS",
    krakenValue: "STRS",
    lpgValue: StreetType.STAIRS,
  },
  {
    name: "State highway",
    pafValue: "SHWY",
    krakenValue: "SHWY",
    lpgValue: StreetType.STATE_HIGHWAY,
  },
  {
    name: "Steps",
    pafValue: "STPS",
    krakenValue: "STPS",
    lpgValue: StreetType.STEPS,
  },
  {
    name: "Strand",
    pafValue: "STRA",
    krakenValue: "STRA",
    lpgValue: StreetType.STRAND,
  },
  {
    name: "Street",
    pafValue: "ST",
    krakenValue: "ST",
    lpgValue: StreetType.STREET,
  },
  {
    name: "Strip",
    pafValue: "STRP",
    krakenValue: "STRP",
    lpgValue: StreetType.STRIP,
  },
  {
    name: "Subway",
    pafValue: "SBWY",
    krakenValue: "SBWY",
    lpgValue: StreetType.SUBWAY,
  },
  {
    name: "Tarn",
    pafValue: "TARN",
    krakenValue: "TARN",
    lpgValue: StreetType.TARN,
  },
  {
    name: "Terrace",
    pafValue: "TCE",
    krakenValue: "TCE",
    lpgValue: StreetType.TERRACE,
  },
  {
    name: "Thoroughfare",
    pafValue: "THOR",
    krakenValue: "THOR",
    lpgValue: StreetType.THOROUGHFARE,
  },
  {
    name: "Tollway",
    pafValue: "TLWY",
    krakenValue: "TLWY",
    lpgValue: StreetType.TOLLWAY,
  },
  {
    name: "Top",
    pafValue: "TOP",
    krakenValue: "TOP",
    lpgValue: StreetType.TOP,
  },
  {
    name: "Tor",
    pafValue: "TOR",
    krakenValue: "TOR",
    lpgValue: StreetType.TOR,
  },
  {
    name: "Towers",
    pafValue: "TWRS",
    krakenValue: "TWRS",
    lpgValue: StreetType.TOWERS,
  },
  {
    name: "Track",
    pafValue: "TRK",
    krakenValue: "TRK",
    lpgValue: StreetType.TRACK,
  },
  {
    name: "Trail",
    pafValue: "TRL",
    krakenValue: "TRL",
    lpgValue: StreetType.TRAIL,
  },
  {
    name: "Trailer",
    pafValue: "TRLR",
    krakenValue: "TRLR",
    lpgValue: StreetType.TRAILER,
  },
  {
    name: "Triangle",
    pafValue: "TRI",
    krakenValue: "TRI",
    lpgValue: StreetType.TRIANGLE,
  },
  {
    name: "Trunkway",
    pafValue: "TKWY",
    krakenValue: "TKWY",
    lpgValue: StreetType.TRUNKWAY,
  },
  {
    name: "Turn",
    pafValue: "TURN",
    krakenValue: "TURN",
    lpgValue: StreetType.TURN,
  },
  {
    name: "Underpass",
    pafValue: "UPAS",
    krakenValue: "UPAS",
    lpgValue: StreetType.UNDERPASS,
  },
  {
    name: "Upper",
    pafValue: "UPR",
    krakenValue: "UPR",
    lpgValue: StreetType.UPPER,
  },
  {
    name: "Vale",
    pafValue: "VALE",
    krakenValue: "VALE",
    lpgValue: StreetType.VALE,
  },
  {
    name: "Viaduct",
    pafValue: "VDCT",
    krakenValue: "VDCT",
    lpgValue: StreetType.VIADUCT,
  },
  {
    name: "View",
    pafValue: "VIEW",
    krakenValue: "VIEW",
    lpgValue: StreetType.VIEW,
  },
  {
    name: "Villas",
    pafValue: "VLLS",
    krakenValue: "VLLS",
    lpgValue: StreetType.VILLAS,
  },
  {
    name: "Vista",
    pafValue: "VSTA",
    krakenValue: "VSTA",
    lpgValue: StreetType.VISTA,
  },
  {
    name: "Wade",
    pafValue: "WADE",
    krakenValue: "WADE",
    lpgValue: StreetType.WADE,
  },
  {
    name: "Walk",
    pafValue: "WALK",
    krakenValue: "WALK",
    lpgValue: StreetType.WALK,
  },
  {
    name: "Walkway",
    pafValue: "WKWY",
    krakenValue: "WKWY",
    lpgValue: StreetType.WALKWAY,
  },
  {
    name: "Way",
    pafValue: "WAY",
    krakenValue: "WAY",
    lpgValue: StreetType.WAY,
  },
  {
    name: "Wharf",
    pafValue: "WHRF",
    krakenValue: "WHRF",
    lpgValue: StreetType.WHARF,
  },
  {
    name: "Wynd",
    pafValue: "WYND",
    krakenValue: "WYND",
    lpgValue: StreetType.WYND,
  },
  {
    name: "Yard",
    pafValue: "YARD",
    krakenValue: "YARD",
    lpgValue: StreetType.YARD,
  },
];
